import { useMemo, useState } from 'react';
import type { ChartDataset } from 'chart.js';
import { ArrowLeft, Download, RotateCcw, Search, SlidersHorizontal, X } from 'lucide-react';
import { ChartBox } from '../components/ChartBox';
import { BlockerTrendPanel } from '../components/BlockerTrendPanel';
import { KpiCard } from '../components/KpiCard';
import { RankList } from '../components/RankList';
import { RateCell, band, pctText, rate, type Outcome } from '../components/RateCell';
import { Tag } from '../components/Tag';
import { buildBlockerTrend, summarizeBlockers } from '../blockerGroups';
import type { InsightsRecord, InsightsResponse } from '../types';
import { num } from '../utils';
import { RANGE_OPTIONS } from '../ranges';
import { SALES_PARENTS, TARGET_CHILDREN, TARGET_COLOR, TARGET_GROUPS, TARGET_LABEL, TARGET_PARENTS, TARGET_PARENT_OF, parentOf, targetOf } from '../targetGroups';

interface InsightsScreenProps {
  data: InsightsResponse | null;
  loaded: boolean;
  busy: boolean;
  range: string;
  onRange: (range: string) => void;
}

type Gran = 'day' | 'week' | 'month';
type Dim = 'periods' | 'stores' | 'categories' | 'subcats' | 'brands' | 'blockers' | 'stages' | 'tiers' | 'needTypes' | 'priorities';
type Filters = Record<Dim, string[]>;
const EMPTY_FILTERS: Filters = {
  periods: [], stores: [], categories: [], subcats: [], brands: [], blockers: [], stages: [], tiers: [], needTypes: [], priorities: [],
};

// ---- time bucketing (client-side day/week/month) ----
function isoWeek(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const dayNum = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNum + 3);
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((d.getTime() - firstThu.getTime()) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}
function bucketOf(r: InsightsRecord, g: Gran): string {
  const day = r.day || '';
  if (g === 'month') return r.month || day.slice(0, 7);
  if (!day) return r.month || '';
  return g === 'day' ? day : isoWeek(day);
}
function periodLabel(code: string): string {
  if (/^\d{4}-W\d{2}$/.test(code)) return `T${code.slice(6)}/${code.slice(2, 4)}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(code)) return `${code.slice(8)}/${code.slice(5, 7)}`;
  if (/^\d{4}-\d{2}$/.test(code)) return `Th${code.slice(5)}/${code.slice(2, 4)}`;
  return code;
}

const SIMPLE: Partial<Record<Dim, (r: InsightsRecord) => string>> = {
  stores: r => r.store_code || '',
  categories: r => targetOf(r),
  subcats: r => r.ai_subcategory_name || '',
  blockers: r => r.ai_blocker_type || '',
  stages: r => r.ai_deal_stage || '',
  tiers: r => r.ai_value_tier || '',
  needTypes: r => r.ai_need_type || '',
  priorities: r => r.ai_followup_priority || '',
};

const BLOCKER_LABEL: Record<string, string> = {
  out_of_stock: 'Hết hàng', demo_unavailable: 'Thiếu demo', price_budget: 'Giá / ngân sách',
  customer_left_early: 'Khách về sớm', comparing_shops: 'So sánh nơi khác', just_browsing: 'Chỉ dạo xem',
  technical_issue: 'Vướng kỹ thuật', not_in_business: 'Không kinh doanh', internal_approval: 'Chờ duyệt nội bộ',
  waiting_response: 'Chờ phản hồi', missing_contact: 'Thiếu liên hệ', unknown: 'Chưa xác định',
};
const STAGE_LABEL: Record<string, string> = {
  new_consulting: 'Mới tư vấn', followup_needed: 'Cần chăm tiếp', stock_blocked: 'Vướng hàng',
  demo_blocked: 'Vướng demo', deposit_pending: 'Đã cọc', closed_won: 'Đã chốt',
  service_recheck: 'Dịch vụ', lost_unknown: 'Mất dấu',
};
// Funnel order + colour: won → warm → blocked → cold; service sits apart (not a sale).
const STAGE_ORDER = ['closed_won', 'deposit_pending', 'followup_needed', 'new_consulting', 'stock_blocked', 'demo_blocked', 'lost_unknown', 'service_recheck'];
const STAGE_COLOR: Record<string, string> = {
  closed_won: 'var(--green)', deposit_pending: 'var(--teal)', followup_needed: 'var(--blue)', new_consulting: 'var(--indigo)',
  stock_blocked: 'var(--amber)', demo_blocked: 'var(--amber)', lost_unknown: 'var(--red)', service_recheck: 'var(--muted)',
};
const PRIO_LABEL: Record<string, string> = { p0: 'P0 · gấp', p1: 'P1 · cao', p2: 'P2 · theo dõi', p3: 'P3 · thấp' };

function labelFor(dim: Dim, code: string): string {
  if (dim === 'categories') return TARGET_LABEL[code] || code;
  if (dim === 'blockers') return BLOCKER_LABEL[code] || code;
  if (dim === 'stages') return STAGE_LABEL[code] || code;
  if (dim === 'priorities') return PRIO_LABEL[code] || code;
  if (dim === 'periods') return periodLabel(code);
  return code;
}
const DIM_TITLE: Record<Dim, string> = {
  periods: 'Kỳ', stores: 'Cửa hàng', categories: 'Ngành target', subcats: 'Loại sản phẩm', brands: 'Thương hiệu',
  blockers: 'Lý do chưa mua', stages: 'Giai đoạn', tiers: 'Giá trị', needTypes: 'Nhu cầu', priorities: 'Ưu tiên',
};

function countByKey(records: InsightsRecord[], keyFn: (r: InsightsRecord) => string): Array<[string, number]> {
  const m = new Map<string, number>();
  records.forEach(r => { const k = keyFn(r); if (k) m.set(k, (m.get(k) || 0) + 1); });
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}
function countByBrand(records: InsightsRecord[]): Array<[string, number]> {
  const m = new Map<string, number>();
  records.forEach(r => (r.ai_brands || []).forEach(b => { if (b) m.set(b, (m.get(b) || 0) + 1); }));
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}

// Close rate over sales visits only: a service visit is not a lost sale.
function outcomeOf(records: InsightsRecord[]): Outcome {
  const o = { n: records.length, sales: 0, won: 0, lost: 0 };
  records.forEach(r => {
    if (r.ai_deal_stage === 'service_recheck' || targetOf(r) === 'SVC') return;
    o.sales++;
    if (r.ai_deal_stage === 'closed_won') o.won++;
    if (r.ai_deal_stage === 'lost_unknown') o.lost++;
  });
  return o;
}

interface ProdAgg { name: string; brand: string; category: string; count: number; stock: number; high: number; closed: number; }

function inRange(day: string | null | undefined, range: string): boolean {
  if (range === 'all') return true;
  if (!day) return false;
  const d = new Date(`${day}T00:00:00`).getTime();
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const today = now.getTime(); const D = 86400000;
  if (range === 'today') return d === today;
  if (range === 'yesterday') return d === today - D;
  const span: Record<string, number> = { '7d': 7, '28d': 28, '3m': 90, '6m': 180 };
  return d >= today - ((span[range] || 1) - 1) * D;
}

// A period/target cell below this many sales visits is noise (1/2 = 50%), so the CR trend leaves a gap.
const CR_MIN_SALES = 5;

const expandTarget =(code: string): string[] => TARGET_CHILDREN[code] || [code];
const sameSet = (a: string[], b: string[]) => a.length === b.length && b.every(v => a.includes(v));

function csvCell(v: unknown): string {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function InsightsScreen({ data, loaded, busy, range, onRange }: InsightsScreenProps) {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [history, setHistory] = useState<Filters[]>([]);
  const [gran, setGran] = useState<Gran>('day');
  // Close rate per target per day is a handful of sales: the CR trend reads weekly by default.
  const [crGran, setCrGran] = useState<Gran>('week');
  const [query, setQuery] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const [detailLimit, setDetailLimit] = useState(100);
  const [crView, setCrView] = useState<'parents' | 'peri'>('parents');
  const records = data?.records || [];
  const appliedRange = data?.meta?.range || range;
  const rangedRecords = useMemo(() => records.filter(r => inRange(r.day, appliedRange)), [records, appliedRange]);

  const commit = (next: Filters) => { setHistory(h => [...h.slice(-19), filters]); setFilters(next); setDetailLimit(100); };
  const toggle = (dim: Dim, value: string) => {
    if (!value) return;
    const cur = filters[dim];
    commit({ ...filters, [dim]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] });
  };
  const setOne = (dim: Dim, value: string) => commit({ ...filters, [dim]: value ? [value] : [] });
  // A target parent (PERI) filters as all its children; the filter itself only ever holds child codes.
  const isTarget = (code: string) => sameSet(filters.categories, expandTarget(code));
  const setTarget = (code: string) => {
    const clear = !code || isTarget(code);
    commit({ ...filters, categories: clear ? [] : expandTarget(code) });
    // Drilling into PERI shows its split in the CR trend; leaving it goes back to the four targets.
    if (!clear && TARGET_PARENT_OF[expandTarget(code)[0]] === 'PED') setCrView('peri');
    else if (clear) setCrView('parents');
  };
  const setPair = (store: string, category: string) => {
    const same = filters.stores.length === 1 && filters.stores[0] === store && isTarget(category);
    commit({ ...filters, stores: same ? [] : [store], categories: same ? [] : expandTarget(category) });
  };
  const goBack = () => {
    if (!history.length) return;
    setFilters(history[history.length - 1]);
    setHistory(history.slice(0, -1));
  };
  const clearAll = () => { commit(EMPTY_FILTERS); setQuery(''); };
  const setGranularity = (g: Gran) => { setGran(g); setFilters(prev => ({ ...prev, periods: [] })); };
  const changeRange = (v: string) => { onRange(v); setFilters(prev => ({ ...prev, periods: [] })); };
  const activeCount = (Object.keys(filters) as Dim[]).reduce((n, d) => n + filters[d].length, 0) + (query.trim() ? 1 : 0);

  const matches = (r: InsightsRecord, dim: Dim, vals: string[]): boolean => {
    if (vals.length === 0) return true;
    if (dim === 'periods') return vals.includes(bucketOf(r, gran));
    if (dim === 'brands') return (r.ai_brands || []).some(b => vals.includes(b));
    const f = SIMPLE[dim];
    return f ? vals.includes(f(r)) : true;
  };

  const needle = query.trim().toLowerCase();
  const ctx = useMemo(() => {
    const dims = Object.keys(filters) as Dim[];
    const base = needle
      ? rangedRecords.filter(r => `${r.raw_text || ''} ${r.ai_need_summary || ''}`.toLowerCase().includes(needle))
      : rangedRecords;
    const apply = (...except: Dim[]) => base.filter(r => dims.every(d => except.includes(d) || matches(r, d, filters[d])));
    return {
      all: apply(), periods: apply('periods'), stores: apply('stores'), categories: apply('categories'),
      subcats: apply('subcats'), brands: apply('brands'), blockers: apply('blockers'), stages: apply('stages'),
      matrix: apply('stores', 'categories', 'stages'),
      crBase: apply('categories', 'stages'),
    };
  }, [rangedRecords, filters, gran, needle]);

  const filtered = ctx.all;
  const total = filtered.length;
  const outcome = outcomeOf(filtered);
  const highValue = filtered.filter(r => r.high_value_flag).length;
  const stockGap = filtered.filter(r => r.stock_gap || r.demo_gap).length;
  const followup = filtered.filter(r => !r.duplicate_of_capture_id
    && ['p0', 'p1', 'p2'].includes(r.ai_followup_priority || '')
    && (r.ai_deal_stage !== 'closed_won' || r.high_value_flag)).length;

  const periodEntries = useMemo(() => {
    const m = new Map<string, { n: number; won: number; high: number }>();
    ctx.periods.forEach(r => {
      const k = bucketOf(r, gran); if (!k) return;
      const e = m.get(k) || { n: 0, won: 0, high: 0 };
      e.n++; if (r.ai_deal_stage === 'closed_won') e.won++; if (r.high_value_flag) e.high++;
      m.set(k, e);
    });
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
  }, [ctx.periods, gran]);

  const blockerTrendData = useMemo(() => {
    const input = ctx.periods.map(r => ({ period: bucketOf(r, gran), blocker: r.ai_blocker_type, count: 1 }));
    return { points: buildBlockerTrend(input, period => period), summary: summarizeBlockers(input) };
  }, [ctx.periods, gran]);

  const storeOptions = useMemo(() => countByKey(rangedRecords, r => r.store_code || '').map(e => e[0]).sort(), [rangedRecords]);
  const optionsOf = (dim: Dim, src: InsightsRecord[]) => (dim === 'brands' ? countByBrand(src) : countByKey(src, SIMPLE[dim] || (() => '')));
  const catEntries = optionsOf('categories', ctx.categories);
  const blockerEntries = countByKey(ctx.blockers.filter(r => r.ai_blocker_type && r.ai_blocker_type !== 'no_blocker'), r => r.ai_blocker_type || '');
  const stageEntries = countByKey(ctx.stages, r => r.ai_deal_stage || '')
    .sort((a, b) => STAGE_ORDER.indexOf(a[0]) - STAGE_ORDER.indexOf(b[0]));
  const stageTotal = stageEntries.reduce((s, e) => s + e[1], 0) || 1;
  const brandEntries = countByBrand(ctx.brands);
  const subcatEntries = countByKey(ctx.subcats, r => r.ai_subcategory_name || '');

  // Store × category: the one cross-tab a store manager acts on (where do we lose which category?).
  const matrix = useMemo(() => {
    // Target columns: the four sales parents, then PERI's own split.
    const cats = [...SALES_PARENTS, ...TARGET_CHILDREN.PED];
    const stores = countByKey(ctx.matrix, r => r.store_code || '').map(e => e[0]).sort();
    const rows = stores.map(store => {
      const mine = ctx.matrix.filter(r => r.store_code === store);
      return { store, all: outcomeOf(mine), cells: cats.map(cat => ({ cat, o: outcomeOf(mine.filter(r => parentOf(r) === cat || targetOf(r) === cat)) })) };
    });
    return { cats, rows };
  }, [ctx.matrix]);

  const products = useMemo(() => {
    const m = new Map<string, ProdAgg>();
    filtered.forEach(r => (r.ai_products || []).forEach(p => {
      const name = (p?.name || '').trim(); if (!name) return;
      const key = name.toLowerCase();
      let a = m.get(key);
      if (!a) { a = { name, brand: p.brand || '', category: p.category || r.ai_category || '', count: 0, stock: 0, high: 0, closed: 0 }; m.set(key, a); }
      a.count++;
      if (r.stock_gap || r.demo_gap) a.stock++;
      if (r.high_value_flag) a.high++;
      if (r.ai_deal_stage === 'closed_won') a.closed++;
    }));
    return [...m.values()].sort((a, b) => b.count - a.count);
  }, [filtered]);
  const lostDemand = products.filter(p => p.stock > 0).sort((a, b) => b.stock - a.stock).slice(0, 20);

  // Close-rate trend per target. Built from every filter except the category one, so picking a
  // line (which filters the dashboard) never makes the other lines disappear.
  const crTrend = useMemo(() => {
    const series = crView === 'peri' ? TARGET_CHILDREN.PED : SALES_PARENTS;
    const inSeries = (r: InsightsRecord, code: string) => parentOf(r) === code || targetOf(r) === code;
    const periods = [...new Set(ctx.crBase.map(r => bucketOf(r, crGran)).filter(Boolean))].sort();
    const byPeriod = new Map<string, InsightsRecord[]>();
    ctx.crBase.forEach(r => { const k = bucketOf(r, crGran); if (k) byPeriod.set(k, [...(byPeriod.get(k) || []), r]); });
    const point = (rows: InsightsRecord[]) => outcomeOf(rows);
    const lines = series.map(code => ({
      code,
      points: periods.map(p => point((byPeriod.get(p) || []).filter(r => inSeries(r, code)))),
      all: point(ctx.crBase.filter(r => inSeries(r, code))),
    }));
    const overall = periods.map(p => point(byPeriod.get(p) || []));
    return { periods, lines, overall };
  }, [ctx.crBase, crGran, crView]);
  const crValue = (o: Outcome) => (o.sales >= CR_MIN_SALES ? Math.round((o.won / o.sales) * 1000) / 10 : null);

  // A full target parent shows as one chip (PERI), not five child chips.
  const fullParents = TARGET_PARENTS.map(p => p.code).filter(code => (TARGET_CHILDREN[code] || []).length > 1
    && TARGET_CHILDREN[code].every(c => filters.categories.includes(c)));
  const chips = (Object.keys(filters) as Dim[]).flatMap(dim => filters[dim]
    .filter(val => dim !== 'categories' || !fullParents.includes(TARGET_PARENT_OF[val]))
    .map(val => ({ dim, val, parent: false })))
    .concat(fullParents.map(val => ({ dim: 'categories' as Dim, val, parent: true })));
  // Store and target live on the quick bar; only the rest needs a removable chip.
  const DETAIL_DIMS: Dim[] = ['periods', 'subcats', 'stages', 'blockers', 'brands', 'priorities', 'tiers', 'needTypes'];
  const detailChips = chips.filter(c => DETAIL_DIMS.includes(c.dim));
  const inPeri = filters.categories.length > 0 && filters.categories.every(c => TARGET_PARENT_OF[c] === 'PED');
  const detailCount = DETAIL_DIMS.reduce((n, d) => n + filters[d].length, 0);
  const categoryCrumb = fullParents.length === 1 && filters.categories.length === TARGET_CHILDREN[fullParents[0]].length
    ? fullParents[0] : filters.categories.length === 1 ? filters.categories[0] : '';
  const exportCsv = () => {
    const head = ['ngay', 'store', 'nganh_hang', 'loai_san_pham', 'giai_doan', 'ly_do_chua_mua', 'uu_tien', 'noi_dung_bao_goc', 'tom_tat_ai'];
    const lines = filtered.map(r => [r.day, r.store_code, labelFor('categories', targetOf(r)), r.ai_subcategory_name,
      r.ai_deal_stage ? labelFor('stages', r.ai_deal_stage) : '', r.ai_blocker_type && r.ai_blocker_type !== 'no_blocker' ? labelFor('blockers', r.ai_blocker_type) : '',
      r.ai_followup_priority ? labelFor('priorities', r.ai_followup_priority) : '', r.raw_text, r.ai_need_summary].map(csvCell).join(','));
    const blob = new Blob([`﻿${[head.join(','), ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `tu-van-cua-hang-${appliedRange}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (busy && !loaded) return <div className="card empty">Đang tải dữ liệu tư vấn tại cửa hàng...</div>;
  if (loaded && records.length === 0) return <div className="card empty">Chưa có dữ liệu tư vấn trong khoảng thời gian này.</div>;

  const selectOf = (dim: Dim, src: InsightsRecord[], all: string) => {
    const cur = filters[dim];
    const opts = optionsOf(dim, src);
    return (
      <select className={`ins-select ${cur.length ? 'has-value' : ''}`} aria-label={DIM_TITLE[dim]} value={cur.length === 1 ? cur[0] : cur.length ? '__multi' : ''}
        onChange={e => e.target.value !== '__multi' && setOne(dim, e.target.value)}>
        <option value="">{all}</option>
        {cur.length > 1 && <option value="__multi">{DIM_TITLE[dim]}: {cur.length} đã chọn</option>}
        {opts.map(([k, n]) => <option key={k} value={k}>{labelFor(dim, k)} · {num(n)}</option>)}
      </select>
    );
  };

  return (
    <>
      <div className="ins-sticky">
        <nav className={`ins-scopebar ${activeCount ? 'is-filtered' : ''}`} aria-label="Bộ lọc">
          <div className="ins-row">
            <div className="ins-crumbs">
              <b>{filters.stores.length ? filters.stores.join(', ') : 'Toàn chuỗi'}</b>
              {categoryCrumb && <span>› {labelFor('categories', categoryCrumb)}</span>}
              <em>{num(total)} / {num(rangedRecords.length)} lượt</em>
              {detailChips.map(({ dim, val }) => (
                <button key={`${dim}:${val}`} type="button" className="ins-chip" onClick={() => toggle(dim, val)} title="Bỏ lọc này">
                  <span>{DIM_TITLE[dim]}:</span> {labelFor(dim, val)} <X size={12} />
                </button>
              ))}
            </div>
            <div className="ins-actions">
              <select className="ins-range" aria-label="Khoảng thời gian" value={range} disabled={busy} onChange={e => changeRange(e.target.value)}>
                {RANGE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
              <label className={`ins-search ${query ? 'has-value' : ''}`} title="Tìm trong nội dung báo gốc">
                <Search size={14} />
                <input type="search" value={query} placeholder="Tìm sản phẩm, NV, mã đơn…" onChange={e => { setQuery(e.target.value); setDetailLimit(100); }} />
              </label>
              <button type="button" className={`ins-detail-toggle ${moreOpen ? 'is-on' : ''} ${detailCount ? 'has-value' : ''}`} aria-expanded={moreOpen} onClick={() => setMoreOpen(v => !v)} title="Lọc chi tiết: nhóm hàng, giai đoạn, lý do, thương hiệu, ưu tiên">
                <SlidersHorizontal size={15} /><span>Lọc chi tiết</span>{detailCount > 0 && <b>{detailCount}</b>}
              </button>
              <button type="button" className="ins-iconbtn" onClick={goBack} disabled={!history.length} title="Quay lại bộ lọc trước" aria-label="Quay lại"><ArrowLeft size={15} /></button>
              <button type="button" className="ins-clear" onClick={clearAll} disabled={!activeCount} title="Xóa tất cả lọc"><RotateCcw size={15} /><span>Xóa lọc</span></button>
              <button type="button" className="ins-iconbtn" onClick={exportCsv} disabled={!total} title="Xuất CSV theo bộ lọc (SĐT đã ẩn)" aria-label="Xuất CSV"><Download size={15} /></button>
            </div>
          </div>
          <div className="ins-quickbar" role="group" aria-label="Lọc nhanh">
            <div className="ins-seg" role="group" aria-label="Cửa hàng">
              <button type="button" aria-pressed={!filters.stores.length} onClick={() => setOne('stores', '')}>Tất cả</button>
              {storeOptions.map(st => <button key={st} type="button" aria-pressed={filters.stores.includes(st)} onClick={() => toggle('stores', st)}>{st}</button>)}
            </div>
            {inPeri ? (
              // Drilled into PERI: the target row becomes its split, like the CFO breadcrumb.
              <div className="ins-seg" role="group" aria-label="Nhóm PERI">
                <button type="button" onClick={() => setTarget('')} title="Về mọi ngành"><ArrowLeft size={13} /> Mọi ngành</button>
                <button type="button" aria-pressed={isTarget('PED')} onClick={() => setTarget(isTarget('PED') ? '' : 'PED')}>PERI</button>
                {TARGET_CHILDREN.PED.map(c => <button key={c} type="button" aria-pressed={sameSet(filters.categories, [c])} onClick={() => setTarget(sameSet(filters.categories, [c]) ? 'PED' : c)}>{labelFor('categories', c)}</button>)}
              </div>
            ) : (
              <div className="ins-seg" role="group" aria-label="Ngành target">
                <button type="button" aria-pressed={!filters.categories.length} onClick={() => setTarget('')}>Mọi ngành</button>
                {TARGET_PARENTS.map(p => <button key={p.code} type="button" aria-pressed={isTarget(p.code)} onClick={() => setTarget(p.code)} title={p.label}>{p.code === 'OTHER' ? 'Khác' : p.label}</button>)}
              </div>
            )}
          </div>
          {moreOpen && (
            <div className="ins-drawer">
              {selectOf('subcats', ctx.subcats, 'Mọi nhóm hàng chi tiết')}
              {selectOf('stages', ctx.stages, 'Mọi giai đoạn')}
              {selectOf('blockers', ctx.blockers, 'Mọi lý do chưa mua')}
              {selectOf('brands', ctx.brands, 'Mọi thương hiệu')}
              {selectOf('priorities', filtered, 'Mọi mức ưu tiên')}
            </div>
          )}
        </nav>
      </div>

      <section id="ins-overview" className="grid kpis ins-anchor">
        <KpiCard label="Lượt khách" value={num(total)} sub={`${num(outcome.sales)} mua · ${num(total - outcome.sales)} dịch vụ`} tone="blue" />
        <KpiCard label="Tỷ lệ chốt" value={pctText(rate(outcome))} sub={`${num(outcome.won)} chốt / ${num(outcome.sales)} lượt mua`} tone="green" />
        <KpiCard label="Mất dấu" value={num(outcome.lost)} sub={outcome.sales ? `${Math.round((outcome.lost / outcome.sales) * 100)}% lượt mua` : '—'} tone="red" />
        <KpiCard label="Cần chăm sóc" value={num(followup)} sub="Hàng đợi P0/P1/P2" tone="amber" />
        <KpiCard label="Khách giá trị cao" value={num(highValue)} sub="Theo nhu cầu và ngành hàng" tone="violet" />
        <KpiCard label="Thiếu hàng / demo" value={num(stockGap)} sub={`${num(lostDemand.length)} sản phẩm bị chặn`} tone="cyan" />
      </section>

      <section className="grid split space atop">
        <div className="card">
          <div className="section-title">
            <h2>Xu hướng lượt khách &amp; lượt chốt</h2>
            <div className="ins-seg small">
              {(['day', 'week', 'month'] as Gran[]).map(g => (
                <button key={g} type="button" aria-pressed={gran === g} onClick={() => setGranularity(g)}>{g === 'day' ? 'Ngày' : g === 'week' ? 'Tuần' : 'Tháng'}</button>
              ))}
            </div>
          </div>
          <div className="chart">
            <ChartBox
              labels={periodEntries.map(e => periodLabel(e[0]))}
              datasets={[
                { label: 'Lượt khách', data: periodEntries.map(e => e[1].n), type: 'bar', backgroundColor: periodEntries.map(e => (!filters.periods.length || filters.periods.includes(e[0])) ? '#2563eb99' : '#2563eb26'), borderRadius: 4, maxBarThickness: 40 } as unknown as ChartDataset,
                { label: 'Đã chốt', data: periodEntries.map(e => e[1].won), type: 'line', borderColor: '#059669', backgroundColor: '#05966922', borderWidth: 2, tension: 0.35, pointRadius: 2 } as unknown as ChartDataset,
                { label: 'Giá trị cao', data: periodEntries.map(e => e[1].high), type: 'line', borderColor: '#7c3aed', borderWidth: 2, borderDash: [5, 3], tension: 0.35, pointRadius: 2 } as unknown as ChartDataset,
              ]}
              onPointClick={({ index }) => toggle('periods', periodEntries[index][0])}
            />
          </div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Giai đoạn phễu</h2><span>bấm để lọc</span></div>
          <div className="ins-stack" role="group" aria-label="Giai đoạn phễu">
            {stageEntries.map(([k, n]) => (
              <button key={k} type="button" title={`${labelFor('stages', k)} · ${num(n)}`} aria-pressed={filters.stages.includes(k)}
                className={filters.stages.length && !filters.stages.includes(k) ? 'is-dim' : ''}
                style={{ flexGrow: n, background: STAGE_COLOR[k] || 'var(--muted)' }} onClick={() => toggle('stages', k)} />
            ))}
          </div>
          <div className="ins-legend">
            {stageEntries.map(([k, n]) => (
              <button key={k} type="button" aria-pressed={filters.stages.includes(k)} className={filters.stages.length && !filters.stages.includes(k) ? 'is-dim' : ''} onClick={() => toggle('stages', k)}>
                <i style={{ background: STAGE_COLOR[k] || 'var(--muted)' }} />
                <span>{labelFor('stages', k)}</span>
                <b className="tnum">{num(n)}</b>
                <small className="tnum">{Math.round((n / stageTotal) * 100)}%</small>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section id="ins-cr" className="card space ins-anchor">
        <div className="section-title">
          <div>
            <h2>Xu hướng tỷ lệ chốt theo ngành target</h2>
            <span>Chốt ÷ lượt mua mỗi {crGran === 'day' ? 'ngày' : crGran === 'week' ? 'tuần' : 'tháng'} · bấm đường hoặc dòng để lọc cả trang · kỳ dưới {CR_MIN_SALES} lượt mua bỏ trống, không nối</span>
          </div>
          <div className="ins-quickbar">
            <div className="ins-seg small" role="group" aria-label="Nhóm hiển thị">
              <button type="button" aria-pressed={crView === 'parents'} onClick={() => setCrView('parents')}>4 ngành target</button>
              <button type="button" aria-pressed={crView === 'peri'} onClick={() => setCrView('peri')}>PERI chi tiết</button>
            </div>
            <div className="ins-seg small">
              {(['day', 'week', 'month'] as Gran[]).map(g => (
                <button key={g} type="button" aria-pressed={crGran === g} onClick={() => setCrGran(g)}>{g === 'day' ? 'Ngày' : g === 'week' ? 'Tuần' : 'Tháng'}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="grid split atop">
          <div className="chart">
            <ChartBox
              type="line"
              labels={crTrend.periods.map(periodLabel)}
              datasets={[
                ...crTrend.lines.map(l => {
                  const on = !filters.categories.length || expandTarget(l.code).some(c => filters.categories.includes(c));
                  const color = TARGET_COLOR[l.code] || '#64748b';
                  return { label: labelFor('categories', l.code), data: l.points.map(crValue), borderColor: on ? color : `${color}44`, backgroundColor: color, borderWidth: on ? 2.5 : 1.5, cubicInterpolationMode: 'monotone', pointRadius: 3, spanGaps: false, datalabels: { display: false } } as unknown as ChartDataset;
                }),
                { label: 'Tất cả ngành', data: crTrend.overall.map(crValue), borderColor: '#94a3b8', borderDash: [5, 4], borderWidth: 1.5, pointRadius: 0, cubicInterpolationMode: 'monotone', spanGaps: false, datalabels: { display: false } } as unknown as ChartDataset,
              ]}
              options={{ scales: { x: { grid: { display: false } }, y: { beginAtZero: true, suggestedMax: 80, ticks: { callback: (v: string | number) => `${v}%` } } } }}
              onPointClick={({ datasetIndex }) => { const l = crTrend.lines[datasetIndex]; if (l) setTarget(l.code); }}
            />
          </div>
          <div className="scroll">
            <table className="ins-crtable">
              <thead><tr><th>Ngành</th><th className="right">Cả kỳ</th><th className="right">Kỳ trước</th><th className="right">Kỳ gần nhất</th><th className="right">Δ</th></tr></thead>
              <tbody>
                {crTrend.lines.map(l => {
                  const n = l.points.length;
                  const last = n ? crValue(l.points[n - 1]) : null;
                  const prev = n > 1 ? crValue(l.points[n - 2]) : null;
                  const delta = last !== null && prev !== null ? Math.round((last - prev) * 10) / 10 : null;
                  const on = expandTarget(l.code).every(c => filters.categories.includes(c));
                  return (
                    <tr key={l.code} className={on ? 'is-selected' : ''} onClick={() => setTarget(l.code)} title="Bấm để lọc ngành này">
                      <td><span className="ins-stage"><i style={{ background: TARGET_COLOR[l.code] }} />{labelFor('categories', l.code)}</span></td>
                      <td className="right"><span className={`ins-band b-${band(rate(l.all))}`}>{pctText(rate(l.all))}</span><small className="muted tnum"> {num(l.all.won)}/{num(l.all.sales)}</small></td>
                      <td className="right tnum muted">{prev === null ? '—' : `${prev}%`}</td>
                      <td className="right tnum"><b>{last === null ? '—' : `${last}%`}</b></td>
                      <td className={`right tnum ${delta === null ? 'muted' : delta >= 0 ? 'ins-up' : 'ins-down'}`}>{delta === null ? '—' : `${delta > 0 ? '▲' : delta < 0 ? '▼' : '•'} ${Math.abs(delta)}đ`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section id="ins-store" className="card space ins-anchor">
        <div className="section-title">
          <h2>Tỷ lệ chốt · cửa hàng × ngành target</h2>
          <span>Bấm ô để lọc cửa hàng + ngành · tỷ lệ = chốt ÷ lượt mua (không tính dịch vụ)</span>
        </div>
        <div className="ins-band-legend">
          <span className="b-good">≥ 50%</span><span className="b-ok">30–50%</span><span className="b-warn">15–30%</span><span className="b-bad">&lt; 15%</span>
        </div>
        <div className="scroll">
          <table className="ins-matrix">
            <thead><tr><th>Cửa hàng</th><th>Tổng</th>{matrix.cats.map(c => <th key={c}>{labelFor('categories', c)}</th>)}</tr></thead>
            <tbody>
              {matrix.rows.map(row => (
                <tr key={row.store}>
                  <th scope="row">
                    <button type="button" className="ins-link" aria-pressed={filters.stores.includes(row.store)} onClick={() => toggle('stores', row.store)}>{row.store}</button>
                  </th>
                  <td><RateCell o={row.all} active={filters.stores.length === 1 && filters.stores[0] === row.store && !filters.categories.length} onClick={() => toggle('stores', row.store)} /></td>
                  {row.cells.map(({ cat, o }) => (
                    <td key={cat}><RateCell o={o} active={filters.stores.includes(row.store) && isTarget(cat)} onClick={() => setPair(row.store, cat)} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section id="ins-reasons" className="grid split space atop ins-anchor">
        <div className="card">
          <div className="section-title"><h2>Lý do khách chưa mua</h2><span>bấm để lọc</span></div>
          <RankList entries={blockerEntries} selected={filters.blockers} onToggle={value => toggle('blockers', value)} labelFor={value => labelFor('blockers', value)} color="var(--amber)" limit={12} />
        </div>
        <div className="card">
          <div className="section-title"><h2>Ngành target</h2><span>PERI tách theo nhóm · bấm để lọc</span></div>
          <RankList entries={catEntries} selected={filters.categories} onToggle={value => toggle('categories', value)} labelFor={value => labelFor('categories', value)} color="var(--blue)" limit={12} />
        </div>
      </section>

      <BlockerTrendPanel points={blockerTrendData.points} summary={blockerTrendData.summary} periodLabel={periodLabel} />

      <section id="ins-products" className="grid split space atop ins-anchor">
        <div className="card">
          <div className="section-title"><h2>Thương hiệu được hỏi</h2><span>bấm để lọc</span></div>
          <RankList entries={brandEntries} selected={filters.brands} onToggle={value => toggle('brands', value)} labelFor={value => labelFor('brands', value)} color="var(--cyan)" />
        </div>
        <div className="card">
          <div className="section-title"><h2>Loại sản phẩm</h2><span>bấm để lọc</span></div>
          <RankList entries={subcatEntries} selected={filters.subcats} onToggle={value => toggle('subcats', value)} labelFor={value => labelFor('subcats', value)} color="var(--teal)" />
        </div>
      </section>

      <section className="grid split space">
        <div className="card">
          <div className="section-title"><h2>Top sản phẩm khách hỏi</h2><span>{num(products.length)} sản phẩm · demand lineup</span></div>
          <div className="scroll" style={{ maxHeight: 380 }}>
            <table>
              <thead><tr><th>Sản phẩm</th><th>Thương hiệu</th><th className="right">Lượt hỏi</th><th className="right">Đã chốt</th><th className="right">Thiếu hàng</th><th className="right">Giá trị cao</th></tr></thead>
              <tbody>{products.slice(0, 40).map((p, i) => (
                <tr key={`${p.name}-${i}`}>
                  <td>{p.name}</td>
                  <td className="muted">{p.brand || '—'}</td>
                  <td className="right tnum"><b>{num(p.count)}</b></td>
                  <td className="right tnum">{p.closed || '—'}</td>
                  <td className="right tnum">{p.stock ? <Tag tone="red">{p.stock}</Tag> : '—'}</td>
                  <td className="right tnum">{p.high ? <Tag tone="violet">{p.high}</Tag> : '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Cầu chưa đáp ứng (thiếu hàng / demo)</h2><span>cơ hội nhập hàng · trưng bày</span></div>
          <div className="scroll" style={{ maxHeight: 380 }}>
            <table>
              <thead><tr><th>Sản phẩm khách cần</th><th>Thương hiệu</th><th className="right">Số lượt bị chặn</th></tr></thead>
              <tbody>{lostDemand.length === 0 ? (
                <tr><td colSpan={3} className="muted">Không có sản phẩm nào bị chặn theo bộ lọc hiện tại.</td></tr>
              ) : lostDemand.map((p, i) => (
                <tr key={`lost-${p.name}-${i}`}>
                  <td>{p.name}</td>
                  <td className="muted">{p.brand || '—'}</td>
                  <td className="right tnum"><Tag tone="red">{p.stock}</Tag></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      </section>

      <section id="ins-detail" className="card space ins-anchor">
        <div className="section-title">
          <h2>Chi tiết lượt khách</h2>
          <span>{num(filtered.length)} dòng theo bộ lọc · SĐT đã ẩn</span>
        </div>
        <div className="scroll">
          <table className="ins-detail">
            <thead><tr><th>Ngày</th><th>Store</th><th>Ngành target</th><th>Nhóm hàng chi tiết</th><th>Giai đoạn</th><th>Lý do chưa mua</th><th>Ưu tiên</th><th>Nội dung báo gốc (đã ẩn SĐT)</th></tr></thead>
            <tbody>
              {filtered.slice(0, detailLimit).map(r => (
                <tr key={r.capture_id}>
                  <td className="tnum nowrap">{r.day}</td>
                  <td><b>{r.store_code}</b></td>
                  <td><span className="ins-stage"><i style={{ background: TARGET_COLOR[targetOf(r)] || 'var(--muted)' }} />{labelFor('categories', targetOf(r))}</span></td>
                  <td className="muted">{r.ai_subcategory_name || '—'}</td>
                  <td>{r.ai_deal_stage ? <span className="ins-stage"><i style={{ background: STAGE_COLOR[r.ai_deal_stage] || 'var(--muted)' }} />{labelFor('stages', r.ai_deal_stage)}</span> : '—'}</td>
                  <td className="muted">{r.ai_blocker_type && r.ai_blocker_type !== 'no_blocker' ? labelFor('blockers', r.ai_blocker_type) : '—'}</td>
                  <td>{r.ai_followup_priority ? <Tag tone={r.ai_followup_priority === 'p0' ? 'red' : r.ai_followup_priority === 'p1' ? 'amber' : 'gray'}>{labelFor('priorities', r.ai_followup_priority)}</Tag> : '—'}</td>
                  <td className="ins-raw" title={r.ai_need_summary || undefined}>{r.raw_text || r.ai_need_summary || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length > detailLimit && (
          <button type="button" className="ins-more" onClick={() => setDetailLimit(n => n + 200)}>Xem thêm {num(Math.min(200, filtered.length - detailLimit))} dòng · còn {num(filtered.length - detailLimit)}</button>
        )}
      </section>
    </>
  );
}
